import { JSX, lazy, ReactElement, Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router-dom';
import { useToggle } from 'react-use';
import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';

import { selectOrchestrate, selectSignUp } from 'store';

import Error from 'pages/Error';
import AdditionalConditions from 'pages/SignUp/regularFlows/mifSteps/AdditionalConditions';
import PreInsurance from 'pages/SignUp/regularFlows/PreInsurance';
import Terms from 'pages/SignUp/regularFlows/Terms';
import BipolarDisorder from 'pages/SignUp/tripleTherapy/BipolarDisorder';
import BloodPressure from 'pages/SignUp/tripleTherapy/BloodPressure';
import Bupropion from 'pages/SignUp/tripleTherapy/Bupropion';
import Glaucoma from 'pages/SignUp/tripleTherapy/Glaucoma';
import KetogenicDiet from 'pages/SignUp/tripleTherapy/KetogenicDiet';
import KidneyStones from 'pages/SignUp/tripleTherapy/KidneyStones';
import LabWork from 'pages/SignUp/tripleTherapy/LabWork';
// import LabWork from 'pages/SignUp/tripleTherapy/LabWork';
// import LabWorkAlt from 'pages/SignUp/tripleTherapy/LabWorkAlt';
import LacticAcidosis from 'pages/SignUp/tripleTherapy/LacticAcidosis';
import LiverOrKidneyImpairment from 'pages/SignUp/tripleTherapy/LiverOrKidneyImpairment';
import MAOInhibitors from 'pages/SignUp/tripleTherapy/MAOInhibitors';
import PriorSeizures from 'pages/SignUp/tripleTherapy/PriorSeizures';
import SideEffectsWithMetforminBupropion from 'pages/SignUp/tripleTherapy/SideEffectsWithMetforminBupropion/SideEffectsWithMetforminBupropion';
import TTCompleteOnboarding from 'pages/SignUp/tripleTherapy/TTCompleteOnboarding';
import TTResults from 'pages/SignUp/tripleTherapy/TTResults';
import WhatNext from 'pages/SignUp/tripleTherapy/WhatNext';
import WhyTT from 'pages/SignUp/tripleTherapy/WhyTT';
import AddInsurance from 'pages/SignUp/WM/AddInsurance';
import Agreement from 'pages/SignUp/WM/Agreement';
import AppointmentConfirmation from 'pages/SignUp/WM/AppointmentConfirmation';
import BlueLineIntro from 'pages/SignUp/WM/BlueLineIntro';
import Checkout from 'pages/SignUp/WM/Checkout';
import CheckPrescriptionCoverage from 'pages/SignUp/WM/CheckPrescriptionCoverage';
import CompleteOnboarding from 'pages/SignUp/WM/CompleteOnboarding';
import Diabetes from 'pages/SignUp/WM/Diabetes';
import Dob from 'pages/SignUp/WM/Dob';
import GallBladders from 'pages/SignUp/WM/Gallbladders';
import GallStones from 'pages/SignUp/WM/GallStones';
import HealthState from 'pages/SignUp/WM/HealthState';
import HeightAndWeight from 'pages/SignUp/WM/HeightAndWeight';
import IdentityVerification from 'pages/SignUp/WM/IdentityVerification';
import KnowledgeAboutGLP from 'pages/SignUp/WM/KnowledgeAboutGLP';
import MarketingLowerPrice from 'pages/SignUp/WM/MarketingLowerPrice';
import MarketingMoneyBackGuarantee from 'pages/SignUp/WM/MarketingMoneyBackGuarantee';
import MarketingRatedNumberOneForGLP from 'pages/SignUp/WM/MarketingRatedNumberOneForGLP';
import MarketingRegularMeetings from 'pages/SignUp/WM/MarketingRegularMeetings';
import MarketingScreenCompareToOthers from 'pages/SignUp/WM/MarketingScreenCompareToOthers';
import MarketingVideo from 'pages/SignUp/WM/MarketingVideo';
import MarketingWorkingOnApproval from 'pages/SignUp/WM/MarketingWorkingOnApproval';
import MedicalConditions from 'pages/SignUp/WM/MedicalConditions';
import NewTargetWeight from 'pages/SignUp/WM/NewTargetWeight';
import NoticeForCalifornia from 'pages/SignUp/WM/NoticeForCalifornia';
import Pancreatitis from 'pages/SignUp/WM/Pancreatitis';
import PhoneNumber from 'pages/SignUp/WM/PhoneNumber';
import PickVideoOrMessageAppointment from 'pages/SignUp/WM/PickVideoOrMessageAppointment';
import PreferredMedication from 'pages/SignUp/WM/PreferredMedication';
import ScheduleAnAppointment from 'pages/SignUp/WM/ScheduleAnAppointment';
import SelectLab from 'pages/SignUp/WM/SelectLab';
import SetPassword from 'pages/SignUp/WM/SetPassword';
import ShouldUseInsurance from 'pages/SignUp/WM/ShouldUseInsurance';
import State from 'pages/SignUp/WM/State';
import ThyroidNodules from 'pages/SignUp/WM/ThyroidNodules';
import WeightLoseWithoutUncertainty from 'pages/SignUp/WM/WeightLoseWithoutUncertainty';
import WithOrWithoutInsurance from 'pages/SignUp/WM/WithOrWithoutInsurance';
import YouAreInGoodHands from 'pages/SignUp/WM/YouAreInGoodHands';
import YourWeightLossProgram from 'pages/SignUp/WM/YourWeightLossProgram';

import SlideAnimateWrapper from 'shared/animationWrappers/SlideAnimateWrapper';
import InsuranceTypeStepWrapper from 'widgets/signUp/stepWrappers/InsuranceTypeStepWrapper';

import { useAppSelector } from 'hooks';
import { logError } from 'utils/datadog';
import { FlowTypes, SignUpSteps } from 'utils/enums';
import { lazyRetry } from 'utils/helpers';

import { ContentProps } from './content.types';

//WM pages
const Congrats = lazy(() => lazyRetry(() => import('pages/SignUp/WM/Congrats')));
const Address = lazy(() => lazyRetry(() => import('pages/SignUp/WM/Address')));
const Age = lazy(() => lazyRetry(() => import('pages/SignUp/WM/Age')));
const BasicInfo = lazy(() => lazyRetry(() => import('pages/SignUp/WM/BasicInfo')));
const WeightLossGoal = lazy(() => lazyRetry(() => import('pages/SignUp/WM/WeightLossGoal')));
const SexAtBirthStep = lazy(() => lazyRetry(() => import('pages/SignUp/WM/SexAtBirth')));
const CheckMyBenefitsStep = lazy(() =>
  lazyRetry(() => import('pages/SignUp/WM/CheckMyBenefitsStep'))
);
const PersonalDetailsStep = lazy(() =>
  lazyRetry(() => import('pages/SignUp/WM/PersonalDetailsStep'))
);
const DobStep = lazy(() => lazyRetry(() => import('pages/SignUp/WM/DobStep')));
const InsuranceStep = lazy(() => lazyRetry(() => import('pages/SignUp/WM/InsuranceStep')));
const VerifyInsurance = lazy(() => lazyRetry(() => import('pages/SignUp/WM/VerifyInsurance')));
const UnlockYourResults = lazy(() => lazyRetry(() => import('pages/SignUp/WM/UnlockYourResults')));
const BenefitsResult = lazy(() => lazyRetry(() => import('pages/SignUp/WM/BenefitsResult')));

const AppointmentCategory = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/AppointmentCategory'))
);
const AppointmentDetails = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/AppointmentDetails'))
);
const Confirmation = lazy(() => lazyRetry(() => import('pages/SignUp/regularFlows/Confirmation')));
const Intro = lazy(() => lazyRetry(() => import('pages/SignUp/regularFlows/Intro')));
const Allergies = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/mifSteps/Allergies'))
);
const ChronicDiseases = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/mifSteps/ChronicDiseases'))
);
const HealthConditions = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/mifSteps/HealthConditions'))
);
const Medications = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/mifSteps/Medications'))
);
const Pharmacy = lazy(() => lazyRetry(() => import('pages/SignUp/regularFlows/mifSteps/Pharmacy')));
const SocialHistory = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/mifSteps/SocialHistory'))
);
const WomensHealth = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/mifSteps/WomensHealth'))
);
const PreConfirmation = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/PreConfirmation'))
);

const ChooseProviderStepWrapper = lazy(() =>
  lazyRetry(() => import('widgets/signUp/stepWrappers/SelectProviderStepWrapper'))
);
const CreateUserStepWrapper = lazy(() =>
  lazyRetry(() => import('widgets/signUp/stepWrappers/CreateUserStepWrapper'))
);
const InitialInfo = lazy(() => lazyRetry(() => import('pages/SignUp/regularFlows/InitialInfo')));
const MifSummary = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/mifSteps/MifSummary'))
);
const PaymentOptions = lazy(() => lazyRetry(() => import('widgets/signUp/PaymentOptions')));
const PaymentStepWrapper = lazy(() =>
  lazyRetry(() => import('widgets/signUp/stepWrappers/PaymentStepWrapper'))
);
const PlanSelectStepWrapper = lazy(() =>
  lazyRetry(() => import('widgets/signUp/stepWrappers/PlanSelectStepWrapper'))
);

const ResultsStepWrapper = lazy(() =>
  lazyRetry(() => import('widgets/signUp/stepWrappers/ResultsStepWrapper'))
);
const SummaryStepWrapper = lazy(() =>
  lazyRetry(() => import('widgets/signUp/stepWrappers/SummaryStepWrapper'))
);
const SwitchToApp = lazy(() => lazyRetry(() => import('widgets/signUp/SwitchToApp')));
const TwoOptionsFinish = lazy(() =>
  lazyRetry(() => import('pages/SignUp/regularFlows/TwoOptionsFinish'))
);

//Insurance v2 pages
const InsuranceAppointmentsAndMedications = lazy(() =>
  lazyRetry(() => import('pages/SignUp/insurance/InsuranceAppointmentsAndMedications'))
);
const InsuranceForAppointments = lazy(() =>
  lazyRetry(() => import('pages/SignUp/insurance/InsuranceForAppointments'))
);
const InsuranceForMedications = lazy(() =>
  lazyRetry(() => import('pages/SignUp/insurance/InsuranceForMedications'))
);
const InsuranceLowCostOptions = lazy(() =>
  lazyRetry(() => import('pages/SignUp/insurance/InsuranceLowCostOptions'))
);
const InsuranceMedicationsToBeDiscussed = lazy(() =>
  lazyRetry(() => import('pages/SignUp/insurance/InsuranceMedicationsToBeDiscussed'))
);
// v3 insurance
const InsuranceForAppointmentsDiscovery = lazy(() =>
  lazyRetry(() => import('pages/SignUp/insurance/InsuranceForAppointmentsDiscovery'))
);

const renderWithAnimation = (
  component: JSX.Element,
  key: string,
  params?: {
    className?: string;
    isBackAnimation?: boolean;
  }
) => (
  <SlideAnimateWrapper
    className={classNames('mx-auto w-full max-md:h-full', params?.className ?? '')}
    isBackAnimation={!!params?.isBackAnimation}
    key={key}
    src="signup"
  >
    <ErrorBoundary FallbackComponent={Error} onError={logError}>
      <Suspense>{component}</Suspense>
    </ErrorBoundary>
  </SlideAnimateWrapper>
);

const Content: React.FC<ContentProps> = ({
  moveToStep,
  isBackAnimation,
  steps,
  selectedFlow,
  onHandleAdditionalConditions
}) => {
  const {
    firstAppointment: { _id: firstAppointmentId }
  } = useAppSelector(selectSignUp);

  const { stepName = SignUpSteps.State } = useParams<{ stepName: SignUpSteps }>();
  const {
    user: { firstName, lastName }
  } = useAppSelector(selectOrchestrate);
  const [isShowedPaymentMessage, setIsShowedPaymentMessage] = useToggle(false);
  const [category, setCategory] = useState('');

  const content: { [key in SignUpSteps]?: ReactElement } = {
    [SignUpSteps.AdditionalConditions]: renderWithAnimation(
      <AdditionalConditions moveToStep={moveToStep} />,
      SignUpSteps.AdditionalConditions,
      { isBackAnimation }
    ),
    [SignUpSteps.Intro]: renderWithAnimation(
      <Intro moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.Intro,
      { isBackAnimation }
    ),
    [SignUpSteps.VerifyIdentity]: renderWithAnimation(
      <IdentityVerification onContinue={() => moveToStep('next')} />,
      SignUpSteps.VerifyIdentity,
      { isBackAnimation }
    ),
    [SignUpSteps.Insurance]: renderWithAnimation(
      <AddInsurance
        selectedFlow={selectedFlow}
        onContinue={(v?: boolean) => onHandleAdditionalConditions('insurance', !!v)}
      />,
      SignUpSteps.Insurance,
      { isBackAnimation }
    ),
    [SignUpSteps.PreInsurance]: renderWithAnimation(
      <PreInsurance onSelect={(v) => onHandleAdditionalConditions('insurance', v)} />,
      SignUpSteps.PreInsurance,
      { isBackAnimation }
    ),
    [SignUpSteps.InitialInfo]: renderWithAnimation(
      <InitialInfo moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.InitialInfo,
      { isBackAnimation }
    ),
    [SignUpSteps.AppointmentCategory]: renderWithAnimation(
      <AppointmentCategory goToTheNextStep={() => moveToStep('next')} setCategory={setCategory} />,
      SignUpSteps.AppointmentCategory,
      { isBackAnimation }
    ),
    [SignUpSteps.AppointmentDetails]: renderWithAnimation(
      <AppointmentDetails
        category={category}
        goToTheNextStep={() => moveToStep('next')}
        selectedFlow={selectedFlow}
      />,
      SignUpSteps.AppointmentDetails,
      { isBackAnimation }
    ),
    [SignUpSteps.ChooseProvider]: renderWithAnimation(
      <ChooseProviderStepWrapper moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.ChooseProvider,
      { isBackAnimation }
    ),
    [SignUpSteps.PlanSelect]: renderWithAnimation(
      <PlanSelectStepWrapper moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.PlanSelect,
      { isBackAnimation }
    ),
    [SignUpSteps.PaymentOptions]: renderWithAnimation(
      <PaymentOptions moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.PaymentOptions,
      { isBackAnimation }
    ),
    [SignUpSteps.ChronicDiseases]: renderWithAnimation(
      <ChronicDiseases moveToStep={moveToStep} />,
      SignUpSteps.ChronicDiseases,
      { isBackAnimation }
    ),
    confirmation: renderWithAnimation(
      <Confirmation firstAppointmentId={firstAppointmentId} />,
      SignUpSteps.Confirmation,
      { isBackAnimation }
    ),
    [SignUpSteps.CreateNewUser]: renderWithAnimation(
      <CreateUserStepWrapper
        isShowedPaymentMessage={isShowedPaymentMessage}
        key="create-new-user"
        moveToStep={moveToStep}
        selectedFlow={selectedFlow}
        setIsShowedPaymentMessage={() => setIsShowedPaymentMessage(true)}
      />,
      SignUpSteps.CreateNewUser,
      { isBackAnimation }
    ),
    [SignUpSteps.TwoOptionsFinish]: renderWithAnimation(
      <TwoOptionsFinish />,
      SignUpSteps.TwoOptionsFinish,
      { isBackAnimation }
    ),
    [SignUpSteps.HealthConditions]: renderWithAnimation(
      <HealthConditions moveToStep={moveToStep} />,
      SignUpSteps.HealthConditions,
      { isBackAnimation }
    ),
    [SignUpSteps.Payment]: renderWithAnimation(
      <PaymentStepWrapper moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.Payment,
      { isBackAnimation }
    ),
    [SignUpSteps.WMKnowledgeAboutGLP1]: renderWithAnimation(
      <KnowledgeAboutGLP moveToStep={moveToStep} />,
      SignUpSteps.WMKnowledgeAboutGLP1,
      { isBackAnimation }
    ),
    [SignUpSteps.Pharmacy]: renderWithAnimation(
      <Pharmacy moveToStep={moveToStep} />,
      SignUpSteps.Pharmacy,
      { isBackAnimation }
    ),
    [SignUpSteps.Medications]: renderWithAnimation(
      <Medications moveToStep={moveToStep} />,
      SignUpSteps.Medications,
      { isBackAnimation }
    ),
    [SignUpSteps.WomensHealth]: renderWithAnimation(
      <WomensHealth moveToStep={moveToStep} />,
      SignUpSteps.WomensHealth,
      { className: 'flex grow flex-col', isBackAnimation }
    ),
    [SignUpSteps.Allergies]: renderWithAnimation(
      <Allergies moveToStep={moveToStep} />,
      SignUpSteps.Allergies,
      { isBackAnimation }
    ),
    [SignUpSteps.PreConfirmation]: renderWithAnimation(
      <PreConfirmation moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.PreConfirmation,
      { className: 'flex grow flex-col', isBackAnimation }
    ),
    [SignUpSteps.MifSummary]: renderWithAnimation(
      <MifSummary moveToStep={moveToStep} selectedFlow={selectedFlow} steps={steps} />,
      SignUpSteps.MifSummary,
      { isBackAnimation }
    ),
    [SignUpSteps.SelectLabs]: renderWithAnimation(
      <SelectLab
        onContinue={() =>
          moveToStep('next', {
            searchParams: '?shouldEmulateBack=true'
          })
        }
      />,
      SignUpSteps.SelectLabs,
      { isBackAnimation }
    ),
    [SignUpSteps.State]: renderWithAnimation(
      <State moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.State,
      { isBackAnimation }
    ),
    [SignUpSteps.BlueLineIntro]: renderWithAnimation(
      <BlueLineIntro moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.BlueLineIntro,
      { isBackAnimation }
    ),
    [SignUpSteps.SocialHistory]: renderWithAnimation(
      <SocialHistory moveToStep={moveToStep} />,
      SignUpSteps.SocialHistory,
      { isBackAnimation }
    ),
    [SignUpSteps.SCResults]: renderWithAnimation(
      <ResultsStepWrapper
        firstAppointmentId={firstAppointmentId}
        moveToStep={moveToStep}
        selectedFlow={selectedFlow}
      />,
      SignUpSteps.SCResults,
      { isBackAnimation }
    ),
    [SignUpSteps.SwitchToApp]: renderWithAnimation(<SwitchToApp />, SignUpSteps.SwitchToApp, {
      isBackAnimation
    }),
    [SignUpSteps.Summary]: renderWithAnimation(
      <SummaryStepWrapper moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.Summary,
      { isBackAnimation }
    ),
    [SignUpSteps.WithOrWithoutInsurance]: renderWithAnimation(
      <WithOrWithoutInsurance onContinue={(v) => onHandleAdditionalConditions('insurance', v)} />,
      SignUpSteps.WithOrWithoutInsurance,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMWeightLoseAim]: renderWithAnimation(
      <WeightLossGoal moveToStep={moveToStep} />,
      SignUpSteps.WMWeightLoseAim,
      { isBackAnimation }
    ),
    [SignUpSteps.WMAge]: renderWithAnimation(
      <Age moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.WMAge,
      {
        className: 'h-full',
        isBackAnimation
      }
    ),
    [SignUpSteps.WMUserHealthState]: renderWithAnimation(
      <HealthState moveToStep={moveToStep} />,
      SignUpSteps.WMUserHealthState,
      { isBackAnimation }
    ),
    [SignUpSteps.WMDiabetesQuestion]: renderWithAnimation(
      <Diabetes moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.WMDiabetesQuestion,
      { isBackAnimation }
    ),
    [SignUpSteps.WMYouAreInGoodHands]: renderWithAnimation(
      <YouAreInGoodHands
        selectedFlow={selectedFlow}
        onContinue={() =>
          onHandleAdditionalConditions(
            'wm-you-are-in-good-hands',
            selectedFlow === FlowTypes.WeightManagementBenefitsCheckNew
          )
        }
      />,
      SignUpSteps.WMYouAreInGoodHands,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMHeightAndWeight]: renderWithAnimation(
      <HeightAndWeight
        moveToStep={moveToStep}
        selectedFlow={selectedFlow}
        onHandleAdditionalConditions={onHandleAdditionalConditions}
      />,
      SignUpSteps.WMHeightAndWeight,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMNewTargetWeight]: renderWithAnimation(
      <NewTargetWeight moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.WMNewTargetWeight,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.InsuranceType]: renderWithAnimation(
      <InsuranceTypeStepWrapper
        moveToStep={moveToStep}
        selectedFlow={selectedFlow}
        onHandleAdditionalConditions={onHandleAdditionalConditions}
      />,
      SignUpSteps.InsuranceType,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMCongrats]: renderWithAnimation(
      <Congrats moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.WMCongrats,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.Agreement]: renderWithAnimation(
      selectedFlow === FlowTypes.WeightManagementFlowInsurance ? (
        <Terms moveToStep={moveToStep} targetName={`${firstName} ${lastName}`} />
      ) : (
        <Agreement moveToStep={moveToStep} selectedFlow={selectedFlow} />
      ),
      SignUpSteps.Agreement,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMBasicInfo]: renderWithAnimation(
      <BasicInfo moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.WMBasicInfo,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMAddress]: renderWithAnimation(
      <Address moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.WMAddress,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMDOB]: renderWithAnimation(
      <Dob moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.WMDOB,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMSexAtBirth]: renderWithAnimation(
      <SexAtBirthStep moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.WMSexAtBirth,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMPancreatitisQuestion]: renderWithAnimation(
      <Pancreatitis moveToStep={moveToStep} />,
      SignUpSteps.WMPancreatitisQuestion,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMThyroidNodulesQuestion]: renderWithAnimation(
      <ThyroidNodules moveToStep={moveToStep} />,
      SignUpSteps.WMThyroidNodulesQuestion,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMGallbladderQuestion]: renderWithAnimation(
      <GallBladders moveToStep={moveToStep} />,
      SignUpSteps.WMGallbladderQuestion,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMGallStonesQuestion]: renderWithAnimation(
      <GallStones moveToStep={moveToStep} />,
      SignUpSteps.WMGallStonesQuestion,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMMedicalConditions]: renderWithAnimation(
      <MedicalConditions moveToStep={moveToStep} />,
      SignUpSteps.WMMedicalConditions,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMPhoneNumber]: renderWithAnimation(
      <PhoneNumber moveToStep={moveToStep} />,
      SignUpSteps.WMPhoneNumber,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMSetPassword]: renderWithAnimation(
      <SetPassword moveToStep={moveToStep} />,
      SignUpSteps.WMSetPassword,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMScheduleAppointment]: renderWithAnimation(
      <ScheduleAnAppointment moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.WMScheduleAppointment,
      { className: 'h-full md:mx-auto md:max-w-[500px]', isBackAnimation }
    ),
    [SignUpSteps.AppointmentConfirmation]: renderWithAnimation(
      <AppointmentConfirmation moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.AppointmentConfirmation,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMCheckout]: renderWithAnimation(
      <Checkout moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.WMCheckout,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.WMCompleteOnboarding]: renderWithAnimation(
      selectedFlow === FlowTypes.TripleTherapy ? (
        <TTCompleteOnboarding />
      ) : (
        <CompleteOnboarding selectedFlow={selectedFlow} />
      ),
      SignUpSteps.WMCompleteOnboarding,
      { className: 'mx-auto h-full max-w-full md:max-w-[500px]', isBackAnimation }
    ),
    [SignUpSteps.WMWeightLoseWithoutUncertainty]: renderWithAnimation(
      <WeightLoseWithoutUncertainty
        selectedFlow={selectedFlow}
        onContinue={() => moveToStep('next')}
      />,
      SignUpSteps.WMWeightLoseWithoutUncertainty,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.MarketingLowerPrice]: renderWithAnimation(
      <MarketingLowerPrice moveToStep={moveToStep} />,
      SignUpSteps.MarketingLowerPrice,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.MarketingCompare]: renderWithAnimation(
      <MarketingScreenCompareToOthers moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.MarketingCompare,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.MarketingRatedNumberOne]: renderWithAnimation(
      <MarketingRatedNumberOneForGLP moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.MarketingRatedNumberOne,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.YourWeightLossProgram]: renderWithAnimation(
      <YourWeightLossProgram moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.YourWeightLossProgram,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.PreferredMedication]: renderWithAnimation(
      <PreferredMedication moveToStep={moveToStep} selectedFlow={selectedFlow} />,
      SignUpSteps.PreferredMedication,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.MarketingWorkingOnApproval]: renderWithAnimation(
      <MarketingWorkingOnApproval moveToStep={moveToStep} />,
      SignUpSteps.MarketingWorkingOnApproval,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.MarketingMoneyBackGuarantee]: renderWithAnimation(
      <MarketingMoneyBackGuarantee moveToStep={moveToStep} />,
      SignUpSteps.MarketingMoneyBackGuarantee,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.PickVideoOrMessageAppointment]: renderWithAnimation(
      <PickVideoOrMessageAppointment
        onContinue={(v) => onHandleAdditionalConditions('appt-type', v === 'message')}
      />,
      SignUpSteps.PickVideoOrMessageAppointment,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.CheckPrescriptionCoverage]: renderWithAnimation(
      <CheckPrescriptionCoverage onContinue={() => moveToStep('next')} />,
      SignUpSteps.CheckPrescriptionCoverage,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.ShouldUseInsurance]: renderWithAnimation(
      <ShouldUseInsurance selectedFlow={selectedFlow} onContinue={() => moveToStep('next')} />,
      SignUpSteps.ShouldUseInsurance,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.NoticeForCalifornia]: renderWithAnimation(
      <NoticeForCalifornia moveToStep={moveToStep} />,
      SignUpSteps.NoticeForCalifornia,
      { className: 'h-full', isBackAnimation }
    ),
    [SignUpSteps.MarketingRegularMeetings]: renderWithAnimation(
      <MarketingRegularMeetings moveToStep={moveToStep} />,
      SignUpSteps.MarketingRegularMeetings,
      { className: 'h-full md:mx-auto md:max-w-[500px]', isBackAnimation }
    ),
    [SignUpSteps.MarketingVideo]: renderWithAnimation(
      <MarketingVideo selectedFlow={selectedFlow} onContinue={() => moveToStep('next')} />,
      SignUpSteps.MarketingVideo
    ),
    // Triple Therapy pages
    [SignUpSteps.WMBloodPressure]: renderWithAnimation(
      <BloodPressure onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMBloodPressure,
      { isBackAnimation }
    ),
    [SignUpSteps.WMWhatNext]: renderWithAnimation(
      <WhatNext onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMWhatNext,
      { isBackAnimation }
    ),
    [SignUpSteps.WMKidneyStones]: renderWithAnimation(
      <KidneyStones selectedFlow={selectedFlow} onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMKidneyStones,
      { isBackAnimation }
    ),
    [SignUpSteps.WMPriorSeizures]: renderWithAnimation(
      <PriorSeizures onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMPriorSeizures,
      { isBackAnimation }
    ),
    [SignUpSteps.WMGlaucoma]: renderWithAnimation(
      <Glaucoma onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMGlaucoma,
      { isBackAnimation }
    ),
    [SignUpSteps.WMMAOInhibitors]: renderWithAnimation(
      <MAOInhibitors onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMMAOInhibitors,
      { isBackAnimation }
    ),
    [SignUpSteps.WMLiverOrKidneyImpairment]: renderWithAnimation(
      <LiverOrKidneyImpairment onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMLiverOrKidneyImpairment,
      { isBackAnimation }
    ),
    [SignUpSteps.WMLacticAcidosis]: renderWithAnimation(
      <LacticAcidosis onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMLacticAcidosis,
      { isBackAnimation }
    ),
    [SignUpSteps.WMBupropion]: renderWithAnimation(
      <Bupropion onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMBupropion,
      { isBackAnimation }
    ),
    [SignUpSteps.BipolarDisorder]: renderWithAnimation(
      <BipolarDisorder onContinue={() => moveToStep('next')} />,
      SignUpSteps.BipolarDisorder,
      { isBackAnimation }
    ),
    [SignUpSteps.WMSideEffectsWithMetforminBupropion]: renderWithAnimation(
      <SideEffectsWithMetforminBupropion onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMSideEffectsWithMetforminBupropion,
      { isBackAnimation }
    ),
    [SignUpSteps.WMKetogenicDiet]: renderWithAnimation(
      <KetogenicDiet onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMKetogenicDiet,
      { isBackAnimation }
    ),
    [SignUpSteps.WMLabWork]: renderWithAnimation(
      <LabWork
        onContinue={() =>
          moveToStep('next', {
            searchParams: '?shouldEmulateBack=true'
          })
        }
        onSelectLabsType={(v: boolean) => onHandleAdditionalConditions('ash-selected', v)}
      />,
      SignUpSteps.WMLabWork,
      {
        isBackAnimation
      }
    ),
    [SignUpSteps.WMTTResults]: renderWithAnimation(
      <TTResults onContinue={() => moveToStep('next')} selectedFlow={selectedFlow} />,
      SignUpSteps.WMTTResults,
      { className: 'md:mx-auto md:max-w-[360px]', isBackAnimation }
    ),
    [SignUpSteps.WMWhyTT]: renderWithAnimation(
      <WhyTT onContinue={() => moveToStep('next')} />,
      SignUpSteps.WMWhyTT,
      { className: 'md:max-w-[500px] md:mx-auto', isBackAnimation }
    ),
    [SignUpSteps.InsuranceAppointmentsAndMedications]: renderWithAnimation(
      <InsuranceAppointmentsAndMedications onContinue={() => moveToStep('next')} />,
      SignUpSteps.InsuranceAppointmentsAndMedications,
      { isBackAnimation }
    ),
    [SignUpSteps.InsuranceForAppointments]: renderWithAnimation(
      selectedFlow === FlowTypes.WeightManagementFlowInsuranceDiscovery ? (
        <InsuranceForAppointmentsDiscovery
          onContinue={(v: boolean) => onHandleAdditionalConditions('insurance', v)}
        />
      ) : (
        <InsuranceForAppointments
          onContinue={(v: boolean) => onHandleAdditionalConditions('insurance', v)}
        />
      ),
      SignUpSteps.InsuranceForAppointments,
      { isBackAnimation }
    ),
    [SignUpSteps.InsuranceMedicationsToBeDiscussed]: renderWithAnimation(
      <InsuranceMedicationsToBeDiscussed
        onContinue={() => onHandleAdditionalConditions('medications-tbd', true)}
      />,
      SignUpSteps.InsuranceMedicationsToBeDiscussed,
      { isBackAnimation }
    ),
    [SignUpSteps.InsuranceForMedications]: renderWithAnimation(
      <InsuranceForMedications
        onContinue={(v: boolean) => onHandleAdditionalConditions('insurance-for-medications', v)}
      />,
      SignUpSteps.InsuranceForMedications,
      { isBackAnimation }
    ),
    [SignUpSteps.InsuranceLowCostOptions]: renderWithAnimation(
      <InsuranceLowCostOptions onContinue={() => moveToStep('next')} />,
      SignUpSteps.InsuranceLowCostOptions,
      { isBackAnimation }
    ),
    [SignUpSteps.CheckMyBenefits]: renderWithAnimation(
      <CheckMyBenefitsStep moveToStep={moveToStep} />,
      SignUpSteps.CheckMyBenefits,
      { isBackAnimation }
    ),
    [SignUpSteps.PersonalDetails]: renderWithAnimation(
      <PersonalDetailsStep moveToStep={moveToStep} />,
      SignUpSteps.PersonalDetails,
      { isBackAnimation }
    ),
    [SignUpSteps.DobStep]: renderWithAnimation(
      <DobStep moveToStep={moveToStep} selectedFlow={FlowTypes.WeightManagementBenefitsCheckNew} />,
      SignUpSteps.DobStep,
      { isBackAnimation }
    ),
    [SignUpSteps.AddInsurance]: renderWithAnimation(
      <InsuranceStep moveToStep={moveToStep} />,
      SignUpSteps.AddInsurance,
      { isBackAnimation }
    ),
    [SignUpSteps.VerifyInsurance]: renderWithAnimation(
      <VerifyInsurance moveToStep={moveToStep} />,
      SignUpSteps.VerifyInsurance,
      { isBackAnimation }
    ),
    [SignUpSteps.UnlockYourResults]: renderWithAnimation(
      <UnlockYourResults moveToStep={moveToStep} />,
      SignUpSteps.UnlockYourResults,
      { isBackAnimation }
    ),
    [SignUpSteps.BenefitsResult]: renderWithAnimation(
      <BenefitsResult
        onContinue={(v: boolean) => onHandleAdditionalConditions('injectable-vs-oral', v)}
      />,
      SignUpSteps.BenefitsResult,
      { isBackAnimation }
    )
  };
  return <AnimatePresence mode="wait">{content[stepName]}</AnimatePresence>;
};

export default Content;
